import RegulationCommentItem from 'components/Dashboard/Regulations/RegulationCommentItem';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import ListItemSkeleton from 'components/Dashboard/ListControl/ListItemSkeleton';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { store } from 'components/Store';

const COMMENTS_PAGE_SIZE = 20;

const RegulationsDocumentCommentsTab = ({
  comments,
  commentsTotal,
  isLoading,
  loadRegulationDocumentComments,
  heightContainer,
}) => {
  const commentsList = comments ?? [];
  const hasMoreComments = commentsList.length < commentsTotal;
  const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
  const commentsScrollbarRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  const { state, dispatch } = useContext(store);
  const { regulationDocumentCommentsScrollPosition } = state;
  const loadMoreComments = useCallback(async () => {
    if (!hasMoreComments || isLoading || isLoadingMoreComments) {
      return;
    }
    setIsLoadingMoreComments(true);
    try {
      const nextPage = Math.floor(commentsList.length / COMMENTS_PAGE_SIZE) + 1;
      await loadRegulationDocumentComments({ pageNumber: nextPage, pageSize: COMMENTS_PAGE_SIZE });
    } finally {
      setIsLoadingMoreComments(false);
    }
  }, [commentsList.length, commentsTotal, isLoading, isLoadingMoreComments, loadRegulationDocumentComments]);

  const handleCommentsScroll = useCallback(
    (scrollValues) => {
      const { scrollTop, scrollHeight, clientHeight } = scrollValues;
      const threshold = 1;
      const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - threshold;
      if (scrolledToBottom) {
        loadMoreComments();
      }
    },
    [loadMoreComments]
  );

  const rememberCommentsPosition = () => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'regulationDocumentCommentsScrollPosition',
      value: commentsScrollbarRef.current?.view?.scrollTop ?? 0,
    });
  };

  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (isLoading) return;
    if (regulationDocumentCommentsScrollPosition === null || regulationDocumentCommentsScrollPosition < 0) return;
    if (!commentsScrollbarRef.current) return;
    if (commentsList.length === 0) return;
    if (heightContainer <= 0) return;

    const targetScrollTop = regulationDocumentCommentsScrollPosition;
    scrollRestoredRef.current = true;

    const apply = () => {
      const scroller = commentsScrollbarRef.current;
      if (!scroller) return;
      scroller.scrollTop(targetScrollTop);
      const view = scroller.view;
      if (view && view.scrollTop !== targetScrollTop) {
        view.scrollTop = targetScrollTop;
      }
      dispatch({ type: 'MODIFY_SECTION', parameter: 'regulationDocumentCommentsScrollPosition', value: null });
    };

    requestAnimationFrame(() => requestAnimationFrame(apply));
  }, [isLoading, commentsList.length, regulationDocumentCommentsScrollPosition, heightContainer, dispatch]);

  return (
    <CustomScrollbar
      className={'simple-scrollbar indented-scrollbar-small'}
      style={{ height: `${heightContainer - 95}px` }}
      minimalThumbYSize={55}
      maximalThumbYSize={100}
      onScroll={handleCommentsScroll}
      ref={commentsScrollbarRef}
    >
      <div className='px-4 pt-4 px-lg-5'>
        <ul className='policy-list px-0 items-list pb-2 all-open-consultations'>
          {isLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <ListItemSkeleton key={`comments-skeleton-${index}`} sectionName='KeyUpdates' />
              ))
            : commentsList.map((comment, index) => (
                <RegulationCommentItem
                  key={comment.id}
                  listRow={{
                    listContext: {
                      item: comment,
                      index,
                      scrollBarRef: commentsScrollbarRef,
                      array: commentsList,
                    },
                    interactions: {
                      itemOnclick: rememberCommentsPosition,
                      showPriorityBadge: true,
                    },
                  }}
                />
              ))}
          {hasMoreComments && commentsList.length > 0 && <ListItemSkeleton sectionName='KeyUpdates' />}
        </ul>
        {!isLoading && commentsList.length === 0 && <p className='mb-0'>No comments found.</p>}
      </div>
    </CustomScrollbar>
  );
};

export default RegulationsDocumentCommentsTab;
